import React from 'react'

import { Box, Grid, Stack, useTheme } from '@mui/material'

import { Skeleton, mediaPlateColor } from '@repo/ui'
import { Header, InfoBlock, InfoItem, InfoRow } from 'components'

/**
 * Loading placeholder for the product detail page (§19 — a skeleton fills the SAME space the
 * loaded content will occupy, so nothing shifts in).
 *
 * ## What it replaces
 *
 * The old loading branch rendered `<Header title="Loading..." />` plus ONE `InfoRow`, against a
 * loaded page that has a title, a status chip, four action buttons, a 650px square gallery, THREE
 * InfoRows, a default-tag panel and two accordions. So the page did not fade in — it jumped:
 * literal "Loading…" text was replaced by a title of a different length, five controls appeared
 * from nothing, and the right column grew from 90px to 666px. It also used `spacing={6} pt={0}`
 * where the loaded page uses `spacing={4} mt={2}`, so even the parts that existed sat in the wrong
 * place.
 *
 * ## Every number here was measured, not guessed
 *
 * Taken off the rendered page (`/products/:id`, a product with one image, at 1512px wide):
 *
 *   title            h5, 31px tall, on the same row as a 74x32 status chip
 *   header actions   Edit 90 / Archive 116 / Delete 109 / Create Certificates 195, all 49 tall
 *   grid             top 150, `spacing={4}`, left md=5 (666w) / right md=7 (933w)
 *   gallery          650x650 square, 12px radius (Slideshow's own)
 *   right column     InfoBlock 254 → gap 12 → default-tag 88 → gap 12 → accordion 108 → gap 12
 *                    → accordion 108   (the 88 is at a 917px column; it grows to 112 at 814px as
 *                    the description wraps, which is why that panel reproduces CONTENT, not a
 *                    height)
 *
 * The InfoBlock is NOT reproduced from those numbers: it renders the REAL `InfoBlock` / `InfoRow` /
 * `InfoItem` with `value={undefined}`, which `InfoItem` already draws as a `<Skeleton width={150} />`.
 * Same components, same paddings, same label text, so its 254px is correct BY CONSTRUCTION and
 * stays correct if someone adds a row to the real page's block. Hardcoding 254 would have been a
 * fourth stale constant in this session.
 *
 * ## Where it deliberately under-reserves
 *
 * The gallery is a bare square. `Slideshow`'s own skeleton adds a five-thumbnail strip beneath it,
 * which is right for a multi-image product and ~100px too tall for a single-image one — and the
 * count is unknowable before the fetch returns. A square is the common denominator: undershooting
 * by a strip that may not appear beats reserving one that usually does not.
 */
export const ProductPageSkeleton = () => {
  const theme = useTheme()

  /**
   * A header action button: the real widths, at the height `headerActionGroupSx` in Header.tsx
   * enforces on a header button.
   *
   * 40, not the 49 this shipped with: the Header's `actionSize` default moved from `large` (49) to
   * `medium` (40), and re-measured on the live page a header button is 40. A literal only because
   * Header does not export it — a `HEADER_ACTION_HEIGHT` constant is the real fix, and until it
   * exists this has to track that file. It does NOT affect footprint (the title row carries its own
   * `minHeight: 49`), so being stale here is a visual mismatch, not a shift.
   */
  const ActionSkeleton = ({ width }: { width: number }) => (
    <Skeleton variant="rounded" width={width} height={40} sx={{ borderRadius: 2 }} />
  )

  /**
   * One collapsed accordion (Description, Details): 108px tall on a `background.paper` surface with
   * the same hairline border, so the two panels do not pop in as new boxes.
   */
  const AccordionSkeleton = () => (
    <Box
      sx={{
        height: 108,
        borderRadius: 3,
        border: `1px solid ${theme.palette.primary[100]}`,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        alignItems: 'flex-start',
        px: 3,
        pt: 5,
        pl: 10.5,
      }}
    >
      <Skeleton width={96} height={18} />
    </Box>
  )

  return (
    <>
      <Header
        // A bar, not the word "Loading…". Text of an unknown length being swapped for text of a
        // different unknown length is the shift this whole file exists to prevent. 248px is the
        // measured width of a representative title ("Rolex Submariner Date").
        title={<Skeleton width={248} height={31} />}
        breadcrumbs={true}
        // A skeleton crumb, NOT omitted. `Header` renders the breadcrumb trail only when this is
        // truthy, so leaving it out gave an EMPTY 8px wayfinding row against the loaded page's
        // ~29px one — measured, that put the whole skeleton 25px too high (grid at 125 vs 150).
        // With a bar here the trail reads "Products / ▭", which is both the right height and the
        // honest statement: we know the parent, not the record.
        breadcrumbsOveride={<Skeleton width={150} height={16} />}
        // The status chip shares the title's row on the loaded page.
        leftComponents={
          <Skeleton variant="rounded" width={74} height={32} sx={{ borderRadius: '999px' }} />
        }
        rightComponents={
          <Stack direction="row" spacing={3} alignItems="center">
            {/* Sync is omitted: it renders only for Shopify-linked products, which is not knowable
                until the fetch returns. Reserving it would leave a 120px hole on every product
                that does not have one — the more common case. */}
            <ActionSkeleton width={90} />
            <ActionSkeleton width={116} />
            <ActionSkeleton width={109} />
            <ActionSkeleton width={195} />
          </Stack>
        }
      />

      {/* `spacing={4}` and `mt: 2` — identical to the loaded page's Grid. */}
      <Grid container spacing={4} sx={{ mt: 2 }}>
        <Grid item xs={12} md={5}>
          {/*
            The aspect ratio lives on a WRAPPER, with the Skeleton filling it at height 100%.
            `aspectRatio` on the Skeleton itself collapses to zero height — MUI's Skeleton computes
            its own height and an aspect-ratio alone gives it nothing to derive from, so the biggest
            element on the page rendered as a hairline and the left column came out empty. Caught
            only by looking at it; every numeric check still passed, because the right column drives
            the row's height.

            NB `Slideshow`'s own loading skeleton uses the collapsing form, so it is likely to have
            the same defect wherever it is relied on (ProductPage previously, CertificatePage).
            Worth checking in the sweep.
          */}
          {/*
            VRG-749 follow-up — the placeholder loads INTO the same plate the loaded
            gallery paints, so the largest box on the page does not change colour as
            it resolves. The square itself is the media plate (the card surface) and
            the Skeleton sits on top of it with MUI's own palette-derived tint, which
            is the pair the loaded state hands over to.
          */}
          <Box
            sx={{
              width: '100%',
              aspectRatio: '1 / 1',
              backgroundColor: mediaPlateColor(theme),
              borderRadius: '12px',
              border: `1px solid ${theme.palette.grey[100]}`,
            }}
          >
            <Skeleton
              variant="rounded"
              sx={{ width: '100%', height: '100%', borderRadius: '12px' }}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Stack spacing={3}>
            {/* The real components — see the note above on construction vs hardcoding. */}
            <InfoBlock>
              <InfoRow>
                <InfoItem label="SKU" value={undefined} tooltip={false} />
                <InfoItem label="Product ID" value={undefined} tooltip={false} />
              </InfoRow>
              <InfoRow>
                <InfoItem label="Product URL" value={undefined} tooltip={false} />
                <InfoItem label="Quantity" value={undefined} tooltip={false} />
              </InfoRow>
              <InfoRow>
                <InfoItem label="Type" value={undefined} tooltip={false} />
                <InfoItem label="Manufactured Date" value={undefined} tooltip={false} />
              </InfoRow>
              {/* VRG-528 — the Activation row. Footprint parity (§19): the real page
                  now has four rows, so a three-row skeleton would let the block grow
                  by one row height on load and push the default-tag panel and both
                  accordions down. */}
              <InfoRow>
                <InfoItem label="Activation" value={undefined} tooltip={false} />
                <InfoItem label="" value={<></>} tooltip={false} />
              </InfoRow>
            </InfoBlock>

            {/*
              Default-tag panel. Shaped like the real one rather than pinned to a height: label +
              description on the left, the "Set Default Tag" action on the right, same padding,
              border and surface.

              NOT a fixed height, which was the first attempt and was wrong — I measured 88 at a
              917px column and the same panel is 112 at 814px, because its description wraps. A
              hardcoded number is only correct at the width it was measured at; reproducing the
              CONTENT lets the skeleton wrap exactly as the real panel does.
            */}
            <Box
              sx={{
                p: 5,
                borderRadius: 3,
                border: `1px solid ${theme.palette.primary[100]}`,
                backgroundColor: theme.palette.background.paper,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={4}
              >
                {/* Measured off the real panel at a 917px column: label 18 tall, description 24
                    (ONE line at desktop width — `lines={2}` was the first attempt and made the
                    panel 98 against the real 88), 6px between them, and a 168x40 action. */}
                <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton width={84} height={18} />
                  <Skeleton height={24} sx={{ maxWidth: 640 }} />
                </Stack>
                <Skeleton
                  variant="rounded"
                  width={168}
                  height={40}
                  sx={{ borderRadius: 2, flexShrink: 0 }}
                />
              </Stack>
            </Box>

            <AccordionSkeleton />
            <AccordionSkeleton />
          </Stack>
        </Grid>
      </Grid>
    </>
  )
}
